import { useEffect, useRef } from "react";
import { uploadTableReplay } from "../api/tableReplayApi";
import {
  DEFAULT_WEB_EVAL_FPS,
  MIN_WEB_EVAL_FPS,
  enforceEvalCaptureFrameRate,
  mixEvalAudioIntoCaptureStream,
  pickEvalVideoMimeType,
  readEvalTrackFrameRate,
} from "../utils/evalVideoEncoding";
import { digitalCropSourceRect } from "../utils/cameraTrackZoom";
import { replayEncodeSettings } from "../utils/liveTrainingSettings";
import {
  TABLE_REPLAY_RING_MAX_MS,
  TABLE_REPLAY_TIMESLICE_MS,
  TABLE_REPLAY_WINDOW_MS,
  TABLE_REPLAY_WINDOW_SEC,
  blobFromReplaySegments,
  bufferedReplayMs,
  isAndroidReplayClient,
  isIosReplayClient,
  segmentsForExport,
  shouldShipSealedAndroidReplay,
} from "../utils/tableReplayBuffer";
import {
  describeTrack,
  probeBlobDurationSec,
  replayDebug,
  replayDebugSnapshot,
} from "../utils/tableReplayDebug";

function sleep(ms) {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function mediaRecorderSupports(type) {
  try {
    return (
      typeof MediaRecorder !== "undefined" &&
      typeof MediaRecorder.isTypeSupported === "function" &&
      MediaRecorder.isTypeSupported(type)
    );
  } catch {
    return false;
  }
}

/**
 * Chrome rejects or freezes a WebM that advertises Opus when the stream has
 * no audio track. Pick a mime that matches the tracks we actually record.
 */
function pickReplayMimeType(hasAudio) {
  const candidates = hasAudio
    ? [
        "video/webm;codecs=vp8,opus",
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8",
        "video/webm",
      ]
    : ["video/webm;codecs=vp8", "video/webm;codecs=vp9", "video/webm"];
  for (const type of candidates) {
    if (mediaRecorderSupports(type)) return type;
  }
  return pickEvalVideoMimeType();
}

/** iOS Safari flushes one blob on stop. Chrome records the live camera itself. */
function prefersParallelReplayRecorder() {
  return !isIosReplayClient();
}

const CHROME_REPLAY_MIN_BYTES = 8192;

/**
 * Canvas capture fps. Desktop always uses the coach target (duplicate frames
 * if the sensor is slower). Android stalls when captureStream asks faster than
 * the sensor, so fall back to 30 there — the server re-encodes up to 60.
 */
function replayCaptureFps(stream, preferredFps = DEFAULT_WEB_EVAL_FPS, android = false) {
  const want = Number(preferredFps) === 30 ? MIN_WEB_EVAL_FPS : DEFAULT_WEB_EVAL_FPS;
  if (!android) return want;
  if (want === MIN_WEB_EVAL_FPS) return MIN_WEB_EVAL_FPS;
  const rate = readEvalTrackFrameRate(stream);
  if (rate != null && rate < DEFAULT_WEB_EVAL_FPS - 1) return MIN_WEB_EVAL_FPS;
  return want;
}

/**
 * Paint the camera into a canvas and capture that.
 * Recording the raw track would keep the sensor size and a ~15fps front camera,
 * and a second MediaRecorder on that track fights the eval encoder.
 *
 * Android Chrome does not decode a video that is not in the document, and
 * captureStream(60) stalls the encoder when frames arrive slower than asked.
 */
function openScaledReplayCapture(
  cameraStream,
  fps,
  {
    android = false,
    width = 854,
    height = 480,
    getCaptureTransform = null,
  } = {}
) {
  const video = document.createElement("video");
  video.muted = true;
  video.defaultMuted = true;
  video.playsInline = true;
  video.setAttribute("playsinline", "");
  video.setAttribute("webkit-playsinline", "");
  video.autoplay = true;
  video.srcObject = cameraStream;
  let unmount = () => {};
  if (android && typeof document !== "undefined" && document.body) {
    video.setAttribute("aria-hidden", "true");
    video.tabIndex = -1;
    // Detached / display:none / 0-size videos stay at readyState 0 on Android,
    // so the canvas is black and the recorder never finishes a real file.
    video.style.cssText = [
      "position:fixed",
      "left:0",
      "top:0",
      "width:160px",
      "height:90px",
      "opacity:0",
      "pointer-events:none",
      "z-index:-1",
    ].join(";");
    // Must live inside the fullscreen live-training page. A video on body
    // is outside that tree and Android Chrome stops decoding it.
    const parent =
      document.querySelector(".lt-page") ||
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.body;
    parent.appendChild(video);
    unmount = () => {
      try {
        video.remove();
      } catch {
        /* ignore */
      }
    };
  }
  const canvas = document.createElement("canvas");
  const w = Math.max(160, Number(width) || 854);
  const h = Math.max(90, Number(height) || 480);
  canvas.width = w;
  canvas.height = h;
  let ctx = null;
  try {
    ctx = canvas.getContext("2d", { alpha: false, desynchronized: true });
  } catch {
    ctx = canvas.getContext("2d", { alpha: false });
  }
  let raf = 0;
  let rvfc = 0;
  const targetFps = Math.max(1, Number(fps) || 30);
  // Android Chrome stalls if captureStream asks for more frames than arrive.
  const minDrawGap = android ? 1000 / targetFps : 0;
  let lastDraw = 0;
  let capturedTrack = null;
  const draw = () => {
    const now = typeof performance !== "undefined" ? performance.now() : Date.now();
    if (minDrawGap && now - lastDraw < minDrawGap) return;
    try {
      if (ctx && video.readyState >= 2 && video.videoWidth > 0) {
        lastDraw = now;
        const xform =
          typeof getCaptureTransform === "function"
            ? getCaptureTransform()
            : null;
        const zoom = Number(xform?.zoom) || 1;
        const digitalZoom =
          !xform?.hardwareZoom && zoom > 1.001 ? zoom : 1;
        const flipH = Boolean(xform?.flipH);
        const flipV = Boolean(xform?.flipV);
        const w = canvas.width;
        const h = canvas.height;
        ctx.save();
        ctx.setTransform(
          flipH ? -1 : 1,
          0,
          0,
          flipV ? -1 : 1,
          flipH ? w : 0,
          flipV ? h : 0
        );
        if (digitalZoom > 1.001) {
          const crop = digitalCropSourceRect(
            video.videoWidth,
            video.videoHeight,
            digitalZoom
          );
          ctx.drawImage(
            video,
            crop.sx,
            crop.sy,
            crop.sw,
            crop.sh,
            0,
            0,
            w,
            h
          );
        } else {
          ctx.drawImage(video, 0, 0, w, h);
        }
        ctx.restore();
        capturedTrack?.requestFrame?.();
      }
    } catch {
      /* ignore a detached frame */
    }
  };
  const loop = () => {
    draw();
    raf = window.requestAnimationFrame(loop);
  };
  try {
    const play = video.play?.();
    if (play && typeof play.catch === "function") play.catch(() => {});
  } catch {
    /* ignore */
  }
  let captured = null;
  try {
    captured = canvas.captureStream(targetFps);
    capturedTrack = captured.getVideoTracks?.()[0] || null;
    mixEvalAudioIntoCaptureStream(captured, cameraStream);
  } catch {
    captured = null;
  }
  if (android && typeof video.requestVideoFrameCallback === "function") {
    const onFrame = () => {
      draw();
      rvfc = video.requestVideoFrameCallback(onFrame);
    };
    rvfc = video.requestVideoFrameCallback(onFrame);
  }
  loop();
  return {
    stream: captured || cameraStream,
    stop() {
      if (raf) window.cancelAnimationFrame(raf);
      raf = 0;
      if (rvfc && typeof video.cancelVideoFrameCallback === "function") {
        video.cancelVideoFrameCallback(rvfc);
      }
      rvfc = 0;
      unmount();
      try {
        video.pause();
        video.srcObject = null;
      } catch {
        /* ignore */
      }
      captured?.getVideoTracks?.().forEach((track) => {
        try {
          track.stop();
        } catch {
          /* ignore */
        }
      });
    },
  };
}

/**
 * H.264 MP4 plays on the coach phone without a transcode wait. WebM is only
 * the fallback when this Android browser cannot record MP4 at all.
 */
function pickAndroidReplayMime(hasAudio) {
  const mp4 = pickChromeReplayMime(hasAudio);
  if (mp4 && /mp4|avc1/i.test(mp4)) return mp4;
  const webm = hasAudio
    ? ["video/webm;codecs=vp8,opus", "video/webm;codecs=vp8", "video/webm"]
    : ["video/webm;codecs=vp8", "video/webm"];
  for (const type of webm) {
    if (mediaRecorderSupports(type)) return type;
  }
  return mp4 || pickEvalVideoMimeType();
}

function pickChromeReplayMime(hasAudio) {
  const candidates = hasAudio
    ? [
        'video/mp4;codecs="avc1.42E01E,mp4a.40.2"',
        "video/mp4;codecs=avc1.42E01E,mp4a.40.2",
        "video/mp4",
        "video/webm;codecs=vp8,opus",
        "video/webm;codecs=vp9,opus",
        "video/webm;codecs=vp8",
        "video/webm",
      ]
    : [
        "video/mp4;codecs=avc1.42E01E",
        "video/mp4;codecs=avc1.4D001E",
        "video/mp4",
        "video/webm;codecs=vp8",
        "video/webm;codecs=vp9",
        "video/webm",
      ];
  for (const type of candidates) {
    if (mediaRecorderSupports(type)) return type;
  }
  return pickEvalVideoMimeType();
}

/**
 * Always pass a bitrate. Chrome often ignores it for H.264, but an uncapped
 * recorder lands at ~3–5 Mbps — never fall back to that.
 */
function openBitrateMediaRecorder(media, mimeType, bitrate) {
  const videoBits = Math.max(200_000, Number(bitrate) || 450_000);
  const keyframes = { videoKeyFrameIntervalDuration: 1000 };
  const withMime = mimeType ? { mimeType } : {};
  const variants = [
    {
      ...withMime,
      videoBitsPerSecond: videoBits,
      bitsPerSecond: videoBits,
      ...keyframes,
    },
    {
      ...withMime,
      videoBitsPerSecond: videoBits,
      audioBitsPerSecond: 32_000,
      bitsPerSecond: videoBits + 32_000,
      ...keyframes,
    },
    { ...withMime, videoBitsPerSecond: videoBits, ...keyframes },
    { videoBitsPerSecond: videoBits, bitsPerSecond: videoBits },
    { videoBitsPerSecond: videoBits },
  ];
  for (const opts of variants) {
    try {
      return new MediaRecorder(media, opts);
    } catch {
      /* try next */
    }
  }
  return null;
}

/**
 * Chrome replay recorder.
 * Record the capture tracks passed in — cloning the live camera while a
 * <video> is already showing it freezes the clone on frame 0. No timeslice:
 * stop() yields one file with a real duration the coach phone can start.
 * Source tracks are never stopped.
 */
function createChromeReplayRing(
  stream,
  bitrate,
  {
    videoOnly = false,
    onError,
    timesliceMs = 0,
    mimePicker = null,
    stopTailMs = 60,
    stopIdleMs = 4000,
    stopEarlyMs = 0,
  } = {}
) {
  const liveTracks = (stream?.getTracks?.() || []).filter(
    (track) => track && track.readyState === "live"
  );
  const video = liveTracks.find((track) => track.kind === "video");
  if (!video) return null;

  const open = (videoOnly) => {
    const tracks = videoOnly
      ? [video]
      : [video, ...liveTracks.filter((track) => track.kind === "audio")];
    const hasAudio = tracks.some((track) => track.kind === "audio");
    const media = new MediaStream(tracks);
    const mimeType = (mimePicker || pickChromeReplayMime)(hasAudio);
    return openBitrateMediaRecorder(media, mimeType, bitrate);
  };

  // Prefer video-only: audio is unused on the coach review clip and Chrome
  // bitrate caps are more reliable without an audio track.
  let recorder = videoOnly
    ? open(true)
    : open(true) || open(false);
  if (!recorder) {
    replayDebug("ring_open_failed", { videoOnly, track: describeTrack(video) });
    return null;
  }
  const mime = recorder.mimeType || "video/webm";
  const chunks = [];
  let failed = false;
  let stopRequested = false;
  let startedAt = 0;
  /** Set when the browser stopped the recorder without us asking. */
  let diedAt = 0;
  recorder.ondataavailable = (event) => {
    if (event.data && event.data.size) {
      chunks.push(event.data);
      replayDebug("ring_chunk", {
        bytes: event.data.size,
        atMs: startedAt ? Date.now() - startedAt : 0,
        n: chunks.length,
      });
    }
  };
  recorder.onerror = (event) => {
    failed = true;
    replayDebug("ring_error", {
      error: String(event?.error?.name || event?.error?.message || "unknown"),
      atMs: startedAt ? Date.now() - startedAt : 0,
    });
    try {
      onError?.();
    } catch {
      /* ignore */
    }
  };
  recorder.onstop = () => {
    if (stopRequested) return;
    diedAt = Date.now();
    replayDebug("ring_died", {
      atMs: startedAt ? diedAt - startedAt : 0,
      bytes: chunks.reduce((sum, blob) => sum + blob.size, 0),
      track: describeTrack(video),
    });
  };
  try {
    if (timesliceMs > 0) recorder.start(timesliceMs);
    else recorder.start();
  } catch {
    try {
      recorder.start();
    } catch (err) {
      replayDebug("ring_start_failed", { error: String(err?.message || err) });
      return null;
    }
  }
  startedAt = Date.now();
  replayDebug("ring_start", {
    mime,
    bitrate,
    videoOnly,
    timesliceMs,
    track: describeTrack(video),
  });

  return {
    recorder,
    mime,
    chrome: true,
    get failed() {
      return failed;
    },
    get diedEarly() {
      return diedAt > 0;
    },
    getBytes: () => chunks.reduce((sum, blob) => sum + blob.size, 0),
    getSegments: () => [],
    requestFlush: () => {},
    stop: () =>
      new Promise((resolve) => {
        let settled = false;
        let sawStop = recorder.state === "inactive";
        stopRequested = true;
        const stopAskedAt = Date.now();
        const finish = () => {
          if (settled) return;
          settled = true;
          const blob = chunks.length ? new Blob(chunks, { type: mime }) : null;
          replayDebug("ring_stopped", {
            ranMs: startedAt ? stopAskedAt - startedAt : 0,
            waitMs: Date.now() - stopAskedAt,
            chunks: chunks.length,
            bytes: blob?.size || 0,
            sawStop,
            died: diedAt > 0,
            failed,
          });
          resolve({
            blob,
            segments: [],
            initChunk: null,
            mime,
            bytes: blob?.size || 0,
          });
        };
        if (sawStop) {
          window.setTimeout(finish, stopTailMs);
          return;
        }
        // Never finish on the first dataavailable alone — on iOS/CriOS that
        // often arrives ~1s in while later clusters still follow onstop.
        recorder.onstop = () => {
          sawStop = true;
          window.setTimeout(finish, stopTailMs);
        };
        try {
          recorder.stop();
        } catch {
          window.setTimeout(finish, stopTailMs);
        }
        if (stopEarlyMs > 0 && stopEarlyMs < stopIdleMs) {
          window.setTimeout(() => {
            if (sawStop && chunks.length) finish();
          }, stopEarlyMs);
        }
        window.setTimeout(finish, stopIdleMs);
      }),
  };
}

/**
 * One continuous MediaRecorder with a 1s timeslice ring.
 * Export always stops the recorder first so the WebM is finalized — pruned
 * timeslice concat without stop() yields stuck-frame / unseekable clips.
 *
 * Clones the live camera video and audio tracks. Eval / KoT MediaRecorders keep
 * recording the source stream; stopping the ring never stops those tracks.
 * iOS, which often cannot run a second recorder, falls back to the live eval buffer.
 * Chrome uses createChromeReplayRing instead of this timeslice recorder.
 */
function createReplayRing(stream, { bitrate }) {
  const liveTracks = (stream?.getTracks?.() || []).filter(
    (track) => track && track.readyState === "live"
  );
  const video = liveTracks.find((track) => track.kind === "video");
  if (!video) return null;

  const clones = [];
  try {
    clones.push(video.clone());
    liveTracks
      .filter((track) => track.kind === "audio")
      .forEach((track) => {
        try {
          clones.push(track.clone());
        } catch {
          /* video-only is still playable */
        }
      });
  } catch {
    clones.forEach((track) => {
      try {
        track.stop();
      } catch {
        /* ignore */
      }
    });
    return null;
  }

  const hasAudio = clones.some((track) => track.kind === "audio");
  const media = new MediaStream(clones);
  const mimeType = pickReplayMimeType(hasAudio);
  const releaseClones = () => {
    clones.forEach((track) => {
      try {
        track.stop();
      } catch {
        /* ignore */
      }
    });
  };
  const recorder = openBitrateMediaRecorder(media, mimeType, bitrate);
  if (!recorder) {
    releaseClones();
    return null;
  }

  /** @type {{ blob: Blob, endedAt: number, durationMs: number }[]} */
  const segments = [];
  let initChunk = null;
  let bytes = 0;
  const mime = recorder.mimeType || mimeType || "video/webm";

  recorder.ondataavailable = (event) => {
    if (!event.data || !event.data.size) return;
    const endedAt = Date.now();
    const row = {
      blob: event.data,
      endedAt,
      durationMs: TABLE_REPLAY_TIMESLICE_MS,
    };
    // Keep every chunk for this session — WebM cannot drop early clusters.
    if (!initChunk) initChunk = { blob: event.data, endedAt };
    segments.push(row);
    bytes += event.data.size;
  };

  recorder.onerror = () => {
    /* loop will restart */
  };

  try {
    recorder.start(TABLE_REPLAY_TIMESLICE_MS);
  } catch {
    try {
      recorder.start();
    } catch {
      releaseClones();
      return null;
    }
  }

  return {
    recorder,
    mime,
    getInitChunk: () => initChunk,
    getSegments: () => segments.slice(),
    getBytes: () => bytes,
    requestFlush: () => {
      try {
        if (recorder.state === "recording") recorder.requestData();
      } catch {
        /* ignore */
      }
    },
    stop: () =>
      new Promise((resolve) => {
        let settled = false;
        const finish = () => {
          if (settled) return;
          settled = true;
          releaseClones();
          resolve({
            segments: segments.slice(),
            initChunk,
            mime,
            bytes,
          });
        };
        const chrome = prefersParallelReplayRecorder();
        if (recorder.state === "inactive") {
          window.setTimeout(finish, chrome ? 0 : 200);
          return;
        }
        recorder.onstop = () => {
          // Chrome delivers the last cluster before onstop. iOS delivers it after.
          window.setTimeout(finish, chrome ? 50 : 250);
        };
        if (!chrome) {
          try {
            recorder.requestData();
          } catch {
            /* ignore */
          }
        }
        try {
          recorder.stop();
        } catch {
          window.setTimeout(finish, 200);
        }
        window.setTimeout(finish, 4000);
      }),
  };
}

/**
 * Keeps a rolling last-10s encode on the player camera and uploads it when the
 * coach asks for replay (web). Native iOS uses its own buffer path.
 * Replay is paused while the evaluation video records: a second recorder on
 * the same camera (or sealing the eval recorder) breaks the eval upload.
 */
export function useTableReplayRing({
  active = false,
  getStream,
  getCaptureTransform = null,
  isEvalRecording = null,
  socket = null,
  tsId = null,
  teId = null,
  tableNumber = null,
  userId = null,
  playerId = null,
}) {
  const inflightRef = useRef(new Set());
  const ctxRef = useRef({
    socket,
    tsId,
    teId,
    tableNumber,
    userId,
    playerId,
    getCaptureTransform,
    isEvalRecording,
  });
  ctxRef.current = {
    socket,
    tsId,
    teId,
    tableNumber,
    userId,
    playerId,
    getCaptureTransform,
    isEvalRecording,
  };

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;
    let ring = null;
    let exporting = false;
    let ringStartedAt = 0;
    /** Last finished sealed session (blob + wall duration). */
    let sealed = null;
    /** @type {number} */
    let sealedDurationMs = 0;
    let chromeVideoOnly = false;
    const SEALED_MIN_MS = 8_000;
    /** iOS: record from a canvas once the direct camera-track recorder dies early. */
    let iosForceCanvas = false;
    let iosShortDeaths = 0;
    /** Sent with the upload so the server log shows what the phone chose. */
    let lastDecision = null;
    /** Replay resolution the camera track was last raised for. */
    let cameraSizedFor = null;
    /** Camera stream the current ring records; a lens switch replaces it. */
    let ringSource = null;

    /**
     * Raise the open camera to the coach's replay resolution (never lower it —
     * eval capture needs 720p). Once per setting change: renegotiating the
     * camera on every 12s recycle stalls Android and heats iPhones.
     */
    const ensureCameraResolution = async (stream, encode) => {
      if (cameraSizedFor === encode.resolution) return;
      cameraSizedFor = encode.resolution;
      const track = stream?.getVideoTracks?.()?.[0];
      if (!track?.applyConstraints) return;
      const before = track.getSettings?.() || {};
      const shortSide = Math.min(before.width || 0, before.height || 0);
      if (!shortSide || shortSide >= encode.height) return;
      const landscape = (before.width || 0) >= (before.height || 0);
      const width = landscape ? encode.width : encode.height;
      const height = landscape ? encode.height : encode.width;
      const current = track.getConstraints?.() || {};
      const { aspectRatio: _ignored, ...rest } = current;
      try {
        await track.applyConstraints({
          ...rest,
          width: { ideal: width },
          height: { ideal: height },
        });
      } catch (err) {
        replayDebug("camera_resize_failed", {
          want: `${width}x${height}`,
          error: String(err?.name || err?.message || err),
        });
        return;
      }
      replayDebug("camera_resized", {
        want: `${width}x${height}`,
        before: `${before.width || 0}x${before.height || 0}`,
        after: describeTrack(track),
      });
    };

    const noteRingEnded = (endedRing, ranMs) => {
      if (!isIosReplayClient() || !endedRing) return;
      if (!(endedRing.diedEarly || endedRing.failed) || ranMs >= 4000) return;
      iosShortDeaths += 1;
      if (!iosForceCanvas) {
        iosForceCanvas = true;
        replayDebug("ios_force_canvas", { ranMs, deaths: iosShortDeaths });
      }
    };

    const emitArmed = (bufferedMs) => {
      const ctx = ctxRef.current;
      if (
        !ctx.socket ||
        !ctx.tsId ||
        !ctx.userId ||
        ctx.tableNumber == null ||
        bufferedMs < 1000
      ) {
        return;
      }
      ctx.socket.emit("tableReplayArmed", {
        ts_id: ctx.tsId,
        te_id: ctx.teId,
        table_number: ctx.tableNumber,
        user_id: ctx.userId,
        buffered_seconds: Math.min(
          TABLE_REPLAY_WINDOW_SEC,
          Math.round(bufferedMs / 1000)
        ),
      });
    };

    const usesSealedSessions = () =>
      prefersParallelReplayRecorder() || isIosReplayClient();

    const stopRing = async ({ keepSealed = true } = {}) => {
      if (!ring) return;
      const ranMs = ringStartedAt ? Math.max(0, Date.now() - ringStartedAt) : 0;
      noteRingEnded(ring, ranMs);
      try {
        const failed = Boolean(ring.failed);
        const releaseCapture = ring.releaseCapture;
        const stopped = await ring.stop();
        try {
          releaseCapture?.();
        } catch {
          /* ignore */
        }
        if (
          keepSealed &&
          !failed &&
          usesSealedSessions() &&
          stopped?.blob?.size >= CHROME_REPLAY_MIN_BYTES &&
          ranMs >= SEALED_MIN_MS
        ) {
          sealed = stopped.blob;
          sealedDurationMs = ranMs;
        }
      } catch {
        /* ignore */
      }
      ring = null;
      ringStartedAt = 0;
    };

    const restartRing = async () => {
      await stopRing({ keepSealed: true });
      const ios = isIosReplayClient();
      if (ctxRef.current.isEvalRecording?.()) return false;
      const stream = getStream?.();
      const encode = replayEncodeSettings(ctxRef.current.tsId);
      if (!stream) {
        replayDebug("ring_no_stream", { ios });
        return false;
      }
      const android = isAndroidReplayClient();
      if (stream !== ringSource) cameraSizedFor = null;
      ringSource = stream;
      await ensureCameraResolution(stream, encode);
      const targetFps = encode.fps || DEFAULT_WEB_EVAL_FPS;
      const xform = ctxRef.current.getCaptureTransform?.() || {};
      const zoom = Number(xform.zoom) || 1;
      // iOS: skip the extra canvas/RAF pipeline unless digital zoom/flip needs
      // baking — that second encoder was overheating phones and truncating clips.
      const needCanvas =
        !ios ||
        iosForceCanvas ||
        (zoom > 1.001 && !xform.hardwareZoom) ||
        Boolean(xform.flipH) ||
        Boolean(xform.flipV);

      let captureStream = stream;
      let releaseCapture = null;
      if (needCanvas) {
        // Exact 60fps renegotiation on every recycle stalls Android cameras.
        // iOS also heats up if we applyConstraints every 12s — skip there.
        if (!android && !ios) {
          await enforceEvalCaptureFrameRate(stream, targetFps);
        }
        const captureFps = replayCaptureFps(
          stream,
          ios ? Math.min(targetFps, MIN_WEB_EVAL_FPS) : targetFps,
          android || ios
        );
        const capture = openScaledReplayCapture(stream, captureFps, {
          android: android || ios,
          width: encode.width,
          height: encode.height,
          getCaptureTransform: () => ctxRef.current.getCaptureTransform?.(),
        });
        captureStream = capture.stream;
        releaseCapture = capture.stop;
      }

      if (usesSealedSessions()) {
        ring = createChromeReplayRing(
          captureStream,
          encode.bitrate,
          {
            videoOnly: ios ? true : chromeVideoOnly,
            onError: () => {
              chromeVideoOnly = true;
            },
            timesliceMs: 0,
            mimePicker: android
              ? pickAndroidReplayMime
              : ios
                ? pickChromeReplayMime
                : null,
            // iOS/CriOS: wait for onstop — stopEarlyMs used to finalize after the
            // first ~1s cluster and drop the rest of the clip.
            stopTailMs: android ? 200 : ios ? 400 : 60,
            stopEarlyMs: android ? 700 : 0,
            stopIdleMs: ios ? 6000 : 4000,
          }
        );
      } else {
        ring = createReplayRing(captureStream, encode);
      }
      if (ring) ring.releaseCapture = releaseCapture;
      else releaseCapture?.();
      ringStartedAt = ring ? Date.now() : 0;
      replayDebug("ring_restart", {
        ok: Boolean(ring),
        ios,
        android,
        canvas: needCanvas,
        zoom,
        encode: {
          fps: encode.fps,
          bitrate: encode.bitrate,
          res: encode.resolution,
        },
      });
      return Boolean(ring);
    };

    /** iOS export: measure every usable clip and ship the longest one. */
    const exportIosClip = async () => {
      const candidates = [];
      const previous = sealed;
      const previousMs = sealedDurationMs;
      const sessionMs = ring ? Math.max(0, Date.now() - ringStartedAt) : 0;
      if (previous?.size >= CHROME_REPLAY_MIN_BYTES && previousMs >= SEALED_MIN_MS) {
        candidates.push({ src: "sealed_prev", blob: previous, wallMs: previousMs });
      }
      // Fast path: keep a young recorder running when a full sealed file exists.
      const keepRing = candidates.length > 0 && ring && sessionMs < SEALED_MIN_MS;
      if (ring && !keepRing) {
        const endedRing = ring;
        noteRingEnded(endedRing, sessionMs);
        const releaseCapture = ring.releaseCapture;
        const stopped = await ring.stop();
        try {
          releaseCapture?.();
        } catch {
          /* ignore */
        }
        ring = null;
        ringStartedAt = 0;
        if (!endedRing.failed && stopped?.blob?.size >= CHROME_REPLAY_MIN_BYTES) {
          candidates.push({ src: "ring_current", blob: stopped.blob, wallMs: sessionMs });
        }
      }
      await Promise.all(
        candidates.map(async (c) => {
          c.sec = await probeBlobDurationSec(c.blob);
        })
      );
      const score = (c) => (c.sec != null ? c.sec : c.wallMs / 1000);
      const best = candidates.reduce(
        (a, c) => (!a || score(c) > score(a) ? c : a),
        null
      );
      lastDecision = {
        pick: best?.src || null,
        sessionMs,
        keepRing: Boolean(keepRing),
        iosForceCanvas,
        candidates: candidates.map((c) => ({
          src: c.src,
          bytes: c.blob.size,
          type: c.blob.type,
          wallMs: c.wallMs,
          sec: c.sec != null ? Number(c.sec.toFixed(2)) : null,
        })),
      };
      replayDebug("export_decision", lastDecision);
      if (best?.src === "sealed_prev") {
        sealed = null;
        sealedDurationMs = 0;
      }
      return best ? { blobs: [best.blob], held: best.src === "sealed_prev" ? best : null } : { blobs: [] };
    };

    let chain = Promise.resolve();
    const enqueue = (fn) => {
      const run = chain.then(fn, fn);
      chain = run.then(
        () => undefined,
        () => undefined
      );
      return run;
    };

    const loop = async () => {
      while (!cancelled) {
        if (exporting) {
          await sleep(200);
          continue;
        }
        if (ctxRef.current.isEvalRecording?.()) {
          if (ring) {
            replayDebug("ring_paused_for_eval", {});
            await enqueue(() => stopRing({ keepSealed: false }));
            if (cancelled) return;
          }
          sealed = null;
          sealedDurationMs = 0;
          await sleep(500);
          continue;
        }
        // Lens switch: the canvas path would keep drawing the old, stopped
        // camera, so restart on the new stream and drop the old lens's clip.
        const liveStream = getStream?.();
        if (ring && liveStream && liveStream !== ringSource) {
          replayDebug("ring_camera_changed", {});
          await enqueue(() => stopRing({ keepSealed: false }));
          if (cancelled) return;
          sealed = null;
          sealedDurationMs = 0;
        }
        if (!ring || ring.recorder.state === "inactive") {
          if (ring) {
            replayDebug("ring_inactive", {
              ranMs: Date.now() - ringStartedAt,
              died: Boolean(ring.diedEarly),
            });
          }
          const ok = await enqueue(() => restartRing());
          if (cancelled) return;
          if (!ok) {
            await sleep(500);
            continue;
          }
        }

        // Bound session length so export stays near the 10s window and ffmpeg
        // remux stays fast. Full stop/restart keeps each blob a valid file.
        if (ring && Date.now() - ringStartedAt > TABLE_REPLAY_RING_MAX_MS) {
          await enqueue(() => restartRing());
          if (cancelled) return;
        }

        if (usesSealedSessions()) {
          const elapsed = ring ? Date.now() - ringStartedAt : 0;
          const buffered = Math.max(
            elapsed,
            sealed ? TABLE_REPLAY_WINDOW_MS : 0
          );
          if (buffered >= 1000) emitArmed(buffered);
        } else {
          ring?.requestFlush?.();
          const segments = ring?.getSegments?.() || [];
          const buffered = bufferedReplayMs(segments);
          if (buffered >= 1000 && (ring?.getBytes?.() || 0) > 0) {
            emitArmed(buffered);
          }
        }
        await sleep(400);
      }
    };
    loop();

    const onExport = async (payload) => {
      const ctx = ctxRef.current;
      if (!payload?.replay_id || !ctx.socket) return;
      const target = payload.target_user_id
        ? String(payload.target_user_id)
        : "";
      const mine = [ctx.userId, ctx.playerId]
        .filter((id) => id != null && String(id) !== "")
        .map((id) => String(id));
      if (target && mine.length && !mine.includes(target)) return;
      if (
        payload.table_number != null &&
        ctx.tableNumber != null &&
        Number(payload.table_number) !== Number(ctx.tableNumber)
      ) {
        return;
      }
      if (
        payload.te_id &&
        ctx.teId &&
        String(payload.te_id) !== String(ctx.teId)
      ) {
        return;
      }
      if (inflightRef.current.has(payload.replay_id)) return;

      const fail = (code) => {
        try {
          ctx.socket.emit("tableReplayExportFailed", {
            replay_id: payload.replay_id,
            code,
            te_id: ctx.teId,
            table_number: ctx.tableNumber,
          });
        } catch {
          /* ignore */
        }
      };

      if (ctx.isEvalRecording?.()) {
        replayDebug("export_blocked_eval", { replay_id: payload.replay_id });
        fail("eval_recording");
        return;
      }
      inflightRef.current.add(payload.replay_id);

      exporting = true;
      let heldSealed = null;
      let heldSealedMs = 0;
      try {
        lastDecision = null;
        replayDebug("export_request", {
          replay_id: payload.replay_id,
          ringMs: ring ? Date.now() - ringStartedAt : 0,
          sealedMs: sealed ? sealedDurationMs : 0,
        });
        const blobs = await enqueue(async () => {
          const out = [];
          if (usesSealedSessions() && isIosReplayClient()) {
            const result = await exportIosClip();
            if (result.held) {
              heldSealed = result.held.blob;
              heldSealedMs = result.held.wallMs;
            }
            return result.blobs;
          }
          if (usesSealedSessions()) {
            const android = isAndroidReplayClient();
            const ios = isIosReplayClient();
            const previous = sealed;
            const previousMs = sealedDurationMs;
            let current = null;
            let sessionMs = ring ? Math.max(0, Date.now() - ringStartedAt) : 0;
            // Prefer a finished ~10s+ file over stopping a young recorder.
            // Never ship a short sealed leftover (that was the 1s iOS clip).
            const preferSealed =
              previous?.size >= CHROME_REPLAY_MIN_BYTES &&
              previousMs >= SEALED_MIN_MS &&
              (ios
                ? sessionMs < SEALED_MIN_MS
                : shouldShipSealedAndroidReplay(
                    sessionMs,
                    previous.size,
                    CHROME_REPLAY_MIN_BYTES
                  ));
            if ((android || ios) && preferSealed) {
              heldSealed = previous;
              heldSealedMs = previousMs;
              sealed = null;
              sealedDurationMs = 0;
              out.push(previous);
              return out;
            }
            heldSealed = null;
            sealed = null;
            sealedDurationMs = 0;
            const ranMs = sessionMs;
            if (ring) {
              const failed = Boolean(ring.failed);
              const releaseCapture = ring.releaseCapture;
              const stopped = await ring.stop();
              try {
                releaseCapture?.();
              } catch {
                /* ignore */
              }
              ring = null;
              ringStartedAt = 0;
              if (!failed && stopped?.blob?.size >= CHROME_REPLAY_MIN_BYTES) {
                current = stopped.blob;
              }
            }
            const previousOk =
              previous?.size >= CHROME_REPLAY_MIN_BYTES &&
              previousMs >= SEALED_MIN_MS;
            if (android || ios) {
              // Prefer the longer of current vs a full sealed previous.
              if (current && ranMs >= SEALED_MIN_MS) out.push(current);
              else if (previousOk) out.push(previous);
              else if (current) out.push(current);
            } else if (current && ranMs >= 8000) {
              out.push(current);
            } else {
              if (previousOk) out.push(previous);
              if (current) out.push(current);
            }
            return out;
          }
          if (!ring) return out;
          ring.requestFlush();
          await sleep(120);
          const releaseCapture = ring.releaseCapture;
          const stopped = await ring.stop();
          try {
            releaseCapture?.();
          } catch {
            /* ignore */
          }
          ring = null;
          const exported = segmentsForExport(stopped.segments, {
            now: Date.now(),
            windowMs: TABLE_REPLAY_WINDOW_MS,
            initChunk: stopped.initChunk,
            contiguous: true,
          });
          const blob = blobFromReplaySegments(exported, stopped.mime);
          if (blob?.size) out.push(blob);
          return out;
        });
        // Restart the ring alongside the upload, not before it: on iOS and
        // desktop the restart renegotiates the camera frame rate.
        void enqueue(() => (!cancelled && !ring ? restartRing() : false));
        if (cancelled) {
          replayDebug("export_dropped_not_live", { replay_id: payload.replay_id });
          fail("not_live");
          return;
        }
        if (!blobs.length) {
          replayDebug("export_empty", { decision: lastDecision });
          fail("buffer_empty");
          return;
        }
        const encode = replayEncodeSettings(ctx.tsId || payload.ts_id);
        replayDebug("upload", {
          bytes: blobs.map((b) => b.size),
          types: blobs.map((b) => b.type),
        });
        await uploadTableReplay({
          blobs,
          clientDebug: {
            decision: lastDecision,
            ua: typeof navigator !== "undefined" ? navigator.userAgent : "",
            events: replayDebugSnapshot(40),
          },
          teId: ctx.teId || payload.te_id,
          tsId: ctx.tsId || payload.ts_id,
          replayId: payload.replay_id,
          coachId: payload.coach_id,
          tableNumber: ctx.tableNumber ?? payload.table_number,
          recordingFps: encode.fps,
          videoBitrate: encode.bitrate,
          recordingResolution: encode.resolution,
          replayVideoQuality: encode.quality,
        });
        heldSealed = null;
        heldSealedMs = 0;
      } catch (err) {
        if (heldSealed && !sealed) {
          sealed = heldSealed;
          sealedDurationMs = heldSealedMs;
        }
        fail("upload_failed");
        console.warn("table replay upload failed:", err?.message || err);
        try {
          await enqueue(() => (!cancelled && !ring ? restartRing() : false));
        } catch {
          /* ignore */
        }
      } finally {
        exporting = false;
        inflightRef.current.delete(payload.replay_id);
      }
    };

    socket?.on?.("exportTableReplay", onExport);
    return () => {
      cancelled = true;
      socket?.off?.("exportTableReplay", onExport);
      const current = ring;
      ring = null;
      if (current) {
        void current.stop().finally(() => {
          try {
            current.releaseCapture?.();
          } catch {
            /* ignore */
          }
        });
      }
    };
  }, [active, getStream, socket, tsId, teId, tableNumber, userId, playerId]);
}
